<script lang="ts">
	import { page } from '$app/stores';
	import Tags from '$lib/components/tags.svelte';
	import Time from '$lib/components/time.svelte';
	import { siteBaseUrl, blogBaseUrl, appAuthor, appName } from '$lib/meta';
	import type { Metadata } from '$lib/posts';

	export let title: Metadata['title'];
	export let headline: Metadata['headline'];
	export let tags: Metadata['tags'];
	export let date: Metadata['date'];
	export let dateModified: string | undefined = undefined;
	export let readingTime: string | undefined = undefined;
	export let wordCount: number | undefined = undefined;

	const ogImageUrl = 'https://siposdani87.com/assets/images/favicons/android-chrome-512x512.png';
	$: canonicalUrl = `${blogBaseUrl}${$page.url.pathname}`;
	$: jsonLd = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: title,
		description: headline,
		datePublished: date,
		dateModified: dateModified ?? date,
		dateCreated: date,
		url: canonicalUrl,
		mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
		image: { '@type': 'ImageObject', url: ogImageUrl, width: 512, height: 512 },
		author: {
			'@type': 'Person',
			name: appAuthor,
			url: siteBaseUrl,
			sameAs: ['https://github.com/siposdani87', 'https://twitter.com/siposdani87']
		},
		publisher: {
			'@type': 'Person',
			name: appAuthor,
			url: siteBaseUrl
		},
		keywords: tags?.join(', '),
		...(wordCount ? { wordCount } : {})
	});
	$: breadcrumbLd = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: blogBaseUrl },
			{ '@type': 'ListItem', position: 2, name: 'Posts', item: `${blogBaseUrl}/posts` },
			{ '@type': 'ListItem', position: 3, name: title, item: canonicalUrl }
		]
	});
</script>

<svelte:head>
	<title>{title} | {appName}</title>

	<meta name="description" content={headline} />
	<meta name="keywords" content={tags.join(', ')} />

	<link rel="canonical" href={canonicalUrl} />

	<meta property="og:title" content={title} />
	<meta property="og:description" content={headline} />
	<meta property="og:type" content="article" />
	<meta property="og:url" content={canonicalUrl} />
	<meta property="og:image" content={ogImageUrl} />
	<meta property="og:image:width" content="512" />
	<meta property="og:image:height" content="512" />
	<meta property="og:image:alt" content={title} />
	<meta property="article:published_time" content={date} />
	<meta property="article:author" content={siteBaseUrl} />
	<meta property="article:section" content="Technology" />
	{#each tags as tag}
		<meta property="article:tag" content={tag} />
	{/each}

	<meta name="twitter:title" content={title} />
	<meta name="twitter:description" content={headline} />
	<meta name="twitter:image" content={ogImageUrl} />
	<meta name="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${jsonLd}</script>`}
	{@html `<script type="application/ld+json">${breadcrumbLd}</script>`}
</svelte:head>

<article
	class="prose lg:prose-lg prose-neutral dark:prose-invert prose-a:text-primary prose-headings:tracking-tight prose-headings:font-semibold"
>
	<header class="mb-8 pb-6 border-b border-border">
		<h1>{title}</h1>
		<p class="text-text-secondary">{headline ?? ''}</p>

		<div class="flex flex-wrap items-center gap-2 text-sm text-text-muted not-prose">
			<address class="not-italic">
				By <a href={siteBaseUrl} rel="noreferrer" target="_blank" class="hover:text-primary"
					>D&aacute;niel Sipos</a
				>
			</address>
			<span>&middot;</span>
			<Time datetime={date} />
			{#if readingTime}
				<span>&middot;</span>
				<span>{readingTime}</span>
			{/if}
		</div>
		<div class="mt-3 not-prose">
			<Tags {tags} />
		</div>
	</header>

	<slot />
</article>

<style lang="postcss">
	@reference "../../app.css";

	.prose {
		@apply max-w-full;
	}
</style>
